import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Alert, Spinner } from 'react-bootstrap';
import { FaSync } from 'react-icons/fa';
// import { useLocation, useNavigate } from 'react-router-dom'; // Future: URL-based filter state
import DealSummaryCards from './DealSummaryCards';
import DealFilters from './DealFilters';
import DealTable from './DealTable';
import dealService, { Deal, DealFilters as DealFilterType, DealSummary } from '../../services/dealService';

interface DealDashboardProps {
  // Optional: when provided, overrides the default navigate('/investor-deals/:id')
  // behavior so the parent container (e.g. the Agent Portal Docs Hub tab)
  // can keep deal drill-down inline and preserve its surrounding chrome.
  onDealClick?: (dealId: number) => void;
}

const DealDashboard: React.FC<DealDashboardProps> = ({ onDealClick }) => {
  // const location = useLocation(); // Future: URL-based filter state
  // const navigate = useNavigate(); // Future: URL-based filter state
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deals, setDeals] = useState<Deal[]>([]);
  const [summary, setSummary] = useState<DealSummary | null>(null);
  const [lastScanTime, setLastScanTime] = useState<string | null>(null);
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalDeals, setTotalDeals] = useState(0);
  // const [perPage] = useState(25); // Available if needed for dynamic page sizes
  
  // Load filters from sessionStorage or use defaults
  const getInitialFilters = (): DealFilterType => {
    // Always default to most recent deals first — no grade filter
    // User can apply filters from the UI if they want to narrow down
    return {
      page: 1,
      per_page: 25,
      sort: 'scan_date',
      order: 'desc'
    };
  };
  
  const [filters, setFilters] = useState<DealFilterType>(getInitialFilters());

  useEffect(() => {
    fetchDeals();
    fetchSummary();
  }, [filters]);

  const fetchDeals = async () => {
    try {
      setLoading(true);
      const response = await dealService.getDeals(filters);
      
      if (response.success) {
        setDeals(response.data.deals);
        setSummary(response.data.summary);
        setCurrentPage(response.data.pagination.page);
        setTotalPages(response.data.pagination.total_pages);
        setTotalDeals(response.data.pagination.total_deals);
      } else {
        setError('Failed to fetch deals');
      }
    } catch (err: any) {
      console.error('Error fetching deals:', err);
      setError(err.message || 'Failed to fetch deals');
    } finally {
      setLoading(false);
    }
  };

  const fetchSummary = async () => {
    try {
      const response = await dealService.getSummary();
      if (response.success && response.data.last_scan) {
        const scanTime = new Date(response.data.last_scan.completed_at || response.data.last_scan.started_at);
        setLastScanTime(scanTime.toLocaleString());
      }
    } catch (err) {
      console.error('Error fetching summary:', err);
    }
  };

  const handleFilterChange = (newFilters: Partial<DealFilterType>) => {
    const updatedFilters = {
      ...filters,
      ...newFilters,
      page: newFilters.page || 1 // Reset to page 1 when filters change
    };
    setFilters(updatedFilters);
    
    // Save filters to sessionStorage for persistence
    try {
      sessionStorage.setItem('dealDashboardFilters', JSON.stringify(updatedFilters));
      console.log('💾 Saved filters:', updatedFilters);
    } catch (error) {
      console.warn('Failed to save filters:', error);
    }
  };

  const handleSort = (field: string) => {
    const updatedFilters = {
      ...filters,
      sort: field,
      order: filters.sort === field && filters.order === 'desc' ? 'asc' as const : 'desc' as const,
      page: 1
    };
    setFilters(updatedFilters);
    
    // Save filters to sessionStorage for persistence
    try {
      sessionStorage.setItem('dealDashboardFilters', JSON.stringify(updatedFilters));
      console.log('💾 Saved sort filters:', updatedFilters);
    } catch (error) {
      console.warn('Failed to save sort filters:', error);
    }
  };

  const handlePageChange = (page: number) => {
    const updatedFilters = { ...filters, page };
    setFilters(updatedFilters);
    
    // Save filters to sessionStorage for persistence
    try {
      sessionStorage.setItem('dealDashboardFilters', JSON.stringify(updatedFilters));
      console.log('💾 Saved page filters:', updatedFilters);
    } catch (error) {
      console.warn('Failed to save page filters:', error);
    }
  };

  const handleRefresh = () => {
    fetchDeals();
    fetchSummary();
  };

  return (
    <Container fluid className="py-4">
      <Row className="mb-4">
        <Col>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h2 className="mb-0">
              <span style={{ fontSize: '1.5em' }}>💰</span> Investor Deal Dashboard
            </h2>
            <div className="d-flex align-items-center gap-3">
              {lastScanTime && (
                <small className="text-muted">Last scan: {lastScanTime}</small>
              )}
              <button 
                className="btn btn-sm btn-outline-primary"
                onClick={handleRefresh}
                disabled={loading}
              >
                <FaSync className={loading ? 'fa-spin' : ''} /> Refresh
              </button>
            </div>
          </div>
        </Col>
      </Row>

      {error && (
        <Alert variant="danger" onClose={() => setError(null)} dismissible>
          {error}
        </Alert>
      )}

      {/* Summary Cards */}
      {summary && (
        <DealSummaryCards 
          summary={summary}
          totalDeals={totalDeals}
        />
      )}

      {/* Filters */}
      <Card className="mb-4">
        <Card.Body>
          <DealFilters
            onFilterChange={handleFilterChange}
            currentFilters={filters}
          />
        </Card.Body>
      </Card>

      {/* Deal Table */}
      <Card>
        <Card.Body className="p-0">
          {loading && deals.length === 0 ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
              <p className="mt-3 text-muted">Loading deals...</p>
            </div>
          ) : deals.length > 0 ? (
            <DealTable
              deals={deals}
              currentSort={filters.sort || 'flip_roi'}
              sortOrder={filters.order || 'desc'}
              onSort={handleSort}
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
              onDealUpdate={fetchDeals}
              onDealClick={onDealClick}
            />
          ) : (
            <div className="text-center py-5">
              <p className="text-muted">No deals found matching your criteria</p>
            </div>
          )}
        </Card.Body>
      </Card>
    </Container>
  );
};

export default DealDashboard;